/**
 * CredentialForm Component
 *
 * Modal form for creating/editing credentials with:
 * - Dynamic fields based on credential type (repository, api, custom)
 * - Expiry date picker
 * - Validation
 *
 * @module components/admin/CredentialForm
 */

import { useState, useCallback, useMemo, memo } from 'react';
import {
  X,
  Key,
  Save,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Calendar,
  GitBranch,
  Globe,
  Settings
} from 'lucide-react';

// ============================================
// Constants
// ============================================

const CREDENTIAL_TYPES = {
  REPOSITORY: 'repository',
  API: 'api',
  CUSTOM: 'custom'
};

const TYPE_OPTIONS = [
  { id: CREDENTIAL_TYPES.REPOSITORY, label: 'Repository', icon: GitBranch, description: 'Git tokens, repository access' },
  { id: CREDENTIAL_TYPES.API, label: 'API', icon: Globe, description: 'API keys, service tokens' },
  { id: CREDENTIAL_TYPES.CUSTOM, label: 'Custom', icon: Settings, description: 'Custom key-value pairs' }
];

// ============================================
// Field Components
// ============================================

const FormField = memo(function FormField({
  label,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  error,
  helpText,
  showToggle = false,
  disabled = false
}) {
  const [showValue, setShowValue] = useState(false);
  const inputType = showToggle ? (showValue ? 'text' : 'password') : type;

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-300 mb-1">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <div className="relative">
        <input
          type={inputType}
          name={name}
          value={value || ''}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full px-3 py-2 bg-gray-800 border rounded-lg text-gray-200
            placeholder-gray-500 focus:outline-none focus:ring-2
            ${error
              ? 'border-red-500 focus:ring-red-500/30'
              : 'border-gray-700 focus:ring-indigo-500/30 focus:border-indigo-500'
            }
            ${showToggle ? 'pr-10' : ''}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        />
        {showToggle && (
          <button
            type="button"
            onClick={() => setShowValue(!showValue)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500
                       hover:text-gray-300 transition-colors"
            tabIndex={-1}
          >
            {showValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {helpText && !error && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-400">{error}</p>
      )}
    </div>
  );
});

const TextAreaField = memo(function TextAreaField({
  label,
  name,
  value,
  onChange,
  placeholder,
  rows = 3
}) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-300 mb-1">
        {label}
      </label>
      <textarea
        name={name}
        value={value || ''}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg
                   text-gray-200 placeholder-gray-500 focus:outline-none
                   focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500
                   resize-none"
      />
    </div>
  );
});

// ============================================
// Type Selector Component
// ============================================

const TypeSelector = memo(function TypeSelector({ selectedType, onChange, disabled }) {
  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        Credential Type <span className="text-red-400">*</span>
      </label>
      <div className="grid grid-cols-3 gap-3">
        {TYPE_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = selectedType === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              disabled={disabled}
              className={`
                flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all
                ${isSelected
                  ? 'border-indigo-500 bg-indigo-500/10 text-indigo-300'
                  : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <Icon className="w-6 h-6" />
              <span className="text-sm font-medium">{option.label}</span>
              <span className="text-xs text-center opacity-70">{option.description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

// ============================================
// Custom Key-Value Fields Component
// ============================================

const CustomFieldsEditor = memo(function CustomFieldsEditor({ fields, onChange }) {
  const handleAdd = useCallback(() => {
    onChange([...fields, { key: '', value: '' }]);
  }, [fields, onChange]);

  const handleRemove = useCallback((index) => {
    onChange(fields.filter((_, i) => i !== index));
  }, [fields, onChange]);

  const handleChange = useCallback((index, field, value) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], [field]: value };
    onChange(newFields);
  }, [fields, onChange]);

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        Custom Fields
      </label>
      <div className="space-y-2">
        {fields.map((field, index) => (
          <div key={index} className="flex gap-2">
            <input
              type="text"
              value={field.key}
              onChange={(e) => handleChange(index, 'key', e.target.value)}
              placeholder="Key"
              className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg
                         text-gray-200 placeholder-gray-500 focus:outline-none
                         focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
            />
            <input
              type="password"
              value={field.value}
              onChange={(e) => handleChange(index, 'value', e.target.value)}
              placeholder="Value"
              className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg
                         text-gray-200 placeholder-gray-500 focus:outline-none
                         focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
            />
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="p-2 text-gray-500 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={handleAdd}
        className="mt-2 flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300"
      >
        <Plus className="w-4 h-4" />
        <span>Add Field</span>
      </button>
    </div>
  );
});

// ============================================
// Type-Specific Fields Component
// ============================================

const TypeSpecificFields = memo(function TypeSpecificFields({ type, data, onChange, errors }) {
  if (type === CREDENTIAL_TYPES.REPOSITORY) {
    return (
      <>
        <FormField
          label="Token"
          name="token"
          value={data.token}
          onChange={onChange}
          placeholder="ghp_xxxxxxxxxxxx"
          required
          showToggle
          error={errors.token}
          helpText="Personal access token or deploy key"
        />
        <FormField
          label="URL"
          name="url"
          value={data.url}
          onChange={onChange}
          placeholder="https://github.com/org/repo"
          helpText="Repository or Git server URL (optional)"
        />
        <FormField
          label="Username"
          name="username"
          value={data.username}
          onChange={onChange}
          placeholder="git"
          helpText="Username for authentication (optional)"
        />
      </>
    );
  }

  if (type === CREDENTIAL_TYPES.API) {
    return (
      <>
        <FormField
          label="API Key"
          name="key"
          value={data.key}
          onChange={onChange}
          placeholder="sk-xxxxxxxxxxxx"
          required
          showToggle
          error={errors.key}
          helpText="API key or secret token"
        />
        <FormField
          label="Base URL"
          name="baseUrl"
          value={data.baseUrl}
          onChange={onChange}
          placeholder="https://api.example.com"
          helpText="API base URL (optional)"
        />
      </>
    );
  }

  if (type === CREDENTIAL_TYPES.CUSTOM) {
    return (
      <CustomFieldsEditor
        fields={data.customFields || []}
        onChange={(fields) => onChange('customFields', fields)}
      />
    );
  }

  return null;
});

// ============================================
// Main CredentialForm Component
// ============================================

export function CredentialForm({
  credential,
  scope,
  projectId,
  agentId,
  onSubmit,
  onClose
}) {
  const isEditing = !!credential;

  // Form state
  const [formData, setFormData] = useState({
    name: credential?.name || '',
    type: credential?.type || CREDENTIAL_TYPES.REPOSITORY,
    description: credential?.description || '',
    expiresAt: credential?.expiresAt ? credential.expiresAt.split('T')[0] : '',
    // Type-specific data (will be encrypted)
    token: '',
    url: credential?.url || '',
    username: credential?.username || '',
    key: '',
    baseUrl: credential?.baseUrl || '',
    customFields: credential?.customFields || [{ key: '', value: '' }]
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Handle field change
  const handleChange = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  // Handle type change
  const handleTypeChange = useCallback((type) => {
    setFormData(prev => ({
      ...prev,
      type,
      // Reset type-specific fields
      token: '',
      url: '',
      username: '',
      key: '',
      baseUrl: '',
      customFields: [{ key: '', value: '' }]
    }));
    setErrors({});
  }, []);

  // Validate form
  const validate = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.type === CREDENTIAL_TYPES.REPOSITORY && !formData.token.trim() && !isEditing) {
      newErrors.token = 'Token is required';
    }

    if (formData.type === CREDENTIAL_TYPES.API && !formData.key.trim() && !isEditing) {
      newErrors.key = 'API Key is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isEditing]);

  // Handle submit
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      // Build credential data based on type
      const credentialData = {
        name: formData.name.trim(),
        type: formData.type,
        expiresAt: formData.expiresAt || undefined
      };
      // Only include description if not empty
      if (formData.description.trim()) {
        credentialData.description = formData.description.trim();
      }

      // Add type-specific sensitive data
      if (formData.type === CREDENTIAL_TYPES.REPOSITORY) {
        credentialData.data = {
          token: formData.token || undefined,
          url: formData.url || undefined,
          username: formData.username || undefined
        };
      } else if (formData.type === CREDENTIAL_TYPES.API) {
        credentialData.data = {
          key: formData.key || undefined,
          baseUrl: formData.baseUrl || undefined
        };
      } else if (formData.type === CREDENTIAL_TYPES.CUSTOM) {
        credentialData.data = {
          fields: formData.customFields.filter(f => f.key.trim())
        };
      }

      await onSubmit(credentialData);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validate, onSubmit]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-gray-900 rounded-xl shadow-2xl border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Key className="w-5 h-5 text-indigo-400" />
            <h2 className="text-lg font-semibold text-gray-100">
              {isEditing ? 'Edit Credential' : 'New Credential'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Submit Error */}
          {submitError && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/30
                            rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{submitError}</span>
            </div>
          )}

          {/* Name */}
          <FormField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="gitea-token"
            required
            error={errors.name}
            helpText="Unique identifier for this credential"
          />

          {/* Type Selector */}
          <TypeSelector
            selectedType={formData.type}
            onChange={handleTypeChange}
            disabled={isEditing}
          />

          {/* Type-Specific Fields */}
          <TypeSpecificFields
            type={formData.type}
            data={formData}
            onChange={handleChange}
            errors={errors}
          />

          {/* Description */}
          <TextAreaField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="What is this credential used for?"
          />

          {/* Expiry Date */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Expires At
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="date"
                name="expiresAt"
                value={formData.expiresAt}
                onChange={(e) => handleChange('expiresAt', e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg
                           text-gray-200 focus:outline-none focus:ring-2
                           focus:ring-indigo-500/30 focus:border-indigo-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Optional. Leave empty for non-expiring credentials.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200
                         transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500
                         text-white text-sm font-medium rounded-lg transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isEditing ? 'Update' : 'Create'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CredentialForm;
